import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ContactHero from './components/ContactHero';
import ContactForm from './components/ContactForm';
import ContactMethods from './components/ContactMethods';
import ContactFAQ from './components/ContactFAQ';

const ContactPage = () => {
  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Contact Us - Cynizee Marketing Hub | Family Organization Support</title>
        <meta 
          name="description" 
          content="Get in touch with Cynizee's family-focused support team. Email support@cynizee.com for questions, partnerships, accessibility support, and technical help. Response within 24 hours." 
        />
        <meta name="keywords" content="contact cynizee, family organization support, partnership inquiries, accessibility help, technical support, organizingchaos email" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Contact Us - Cynizee Marketing Hub" />
        <meta property="og:description" content="Connect with our family-focused support team for questions, partnerships, and personalized help with family organization." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cynizee.com/contact" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Us - Cynizee Marketing Hub" />
        <meta name="twitter:description" content="Get personalized support for your family organization journey. Multiple contact methods available." />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://cynizee.com/contact" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Cynizee Marketing Hub" />
      </Helmet>
      <Header />
      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <ContactHero />
          </div>
        </section>

        {/* Contact Form and Methods */}
        <section className="py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Contact Form */}
              <div id="contact-form">
                <div className="mb-8">
                  <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
                    Send Us a Message
                  </h2>
                  <p className="text-text-secondary">
                    Fill out the form below and we'll get back to you within 24 hours. 
                    For urgent matters, please mark your inquiry accordingly.
                  </p>
                </div>
                <ContactForm />
              </div>

              {/* Contact Methods */}
              <div>
                <div className="mb-8">
                  <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
                    Other Ways to Reach Us
                  </h2>
                  <p className="text-text-secondary">
                    Choose the communication method that works best for your needs. 
                    We're here to help in whatever way is most convenient for your family.
                  </p>
                </div>
                <ContactMethods />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <ContactFAQ />
          </div>
        </section>

        {/* Bottom CTA Section */}
        <section className="py-16 bg-gradient-to-r from-primary/5 via-secondary/5 to-accent/5">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <div className="bg-card rounded-2xl p-8 shadow-soft border border-border">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
                Ready to Transform Your Family's Organization?
              </h2>
              <p className="text-text-secondary mb-6">
                Join thousands of families on the waitlist and be among the first to experience 
                the journey from chaos to calm with Cynizee.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/homepage#waitlist"
                  className="inline-flex items-center justify-center px-6 py-3 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 transition-colors duration-200 font-heading font-semibold"
                >
                  Join Waitlist
                </a>
                <a
                  href="/homepage"
                  className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200 font-heading font-semibold"
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      {/* Footer */}
      <footer className="bg-primary text-primary-foreground py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-secondary to-accent rounded-lg flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-white transform rotate-45 rounded-sm"></div>
                </div>
                <div>
                  <h3 className="text-xl font-heading font-bold">Cynizee</h3>
                  <p className="text-sm text-primary-foreground/80">Marketing Hub</p>
                </div>
              </div>
              <p className="text-primary-foreground/80 mb-4 max-w-md">
                Transforming chaotic family life into organized harmony. 
                Built for real families, focused on privacy and child development.
              </p>
              <div className="text-sm text-primary-foreground/60">
                <p>Email: support@cynizee.com</p>
                <p>Business Hours: Mon-Fri, 9:00 AM - 7:00 PM CT</p>
                <p>One-person business with dedicated support</p>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/homepage" className="text-primary-foreground/80 hover:text-white transition-colors">Home</a></li>
                <li><a href="/privacy-policy" className="text-primary-foreground/80 hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/terms-of-service" className="text-primary-foreground/80 hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="/contact" className="text-primary-foreground/80 hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-heading font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li><span className="text-primary-foreground/80">Mon-Fri, 9:00 AM - 7:00 PM CT</span></li>
                <li><span className="text-primary-foreground/80">Email-only support</span></li>
                <li><span className="text-primary-foreground/80">Family-focused help</span></li>
                <li><span className="text-primary-foreground/80">Lock feature expertise</span></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-primary-foreground/20 mt-8 pt-8 text-center">
            <p className="text-sm text-primary-foreground/60">
              © {new Date()?.getFullYear()} Cynizee Marketing Hub. All rights reserved. 
              Built with families in mind.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;